import React, {useCallback, useEffect, useState} from 'react';
import axios from 'axios'
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Container from '@mui/material/Container';
import {MessageChip} from 'component/MessageChip';
import InputAdornment from '@mui/material/InputAdornment';
import {css} from '@emotion/react';
import {zxcvbn, zxcvbnOptions} from "@zxcvbn-ts/core";
import * as zxcvbnCommonPackage from "@zxcvbn-ts/language-common";
import * as zxcvbnDePackage from "@zxcvbn-ts/language-de";
import {FeedbackType} from "@zxcvbn-ts/core/dist/types";
import {IndicatorsComponent} from 'module/SessionInfo/component/password/IndicatorsComponent';
import {PasswordScoreFeedbackComponent} from 'module/SessionInfo/component/password/PasswordScoreFeedbackComponent';
import {selPropGenString} from 'module/Properties/selector/selPropGen'
import {PropID} from 'module/Properties/enum/PropID'
import {useSelector} from 'react-redux'
import {TextFieldProps} from '@mui/material/TextField/TextField'
import {getVisibilityIcon} from 'util/getVisiblityEyeIcon'

export const cssForm = css`
	min-height: 220px;
	width: 100%;
`
export const cssContainer = css`
	width: 100%;
	padding-top: 12px;
	text-align: center;
`
export const cssErrorLabel = css`
	color: #e3391d;
	font-size: small;
`

export function getConfirmColor(pw1?: string, pw2?: string): string {
	if (!pw2) return 'unset'
	return pw1 !== pw2 ? '#e3391d' : '#27a15a';
}

export type PwdStateType = Record<string, boolean>;

export interface MsgProps {
	status: string;
	text: string;
}


interface Indicator {
	score: number;
	feedback: FeedbackType;
}

const options = {
	translations: zxcvbnDePackage?.translations,
	graphs: zxcvbnCommonPackage?.adjacencyGraphs,
	dictionary: {
		...zxcvbnCommonPackage?.dictionary,
		...zxcvbnDePackage?.dictionary,
	},
};

zxcvbnOptions.setOptions(options);

const selPasswordScore = selPropGenString(PropID.evoApps_passwordStrength, '-1');

interface Props {
	redirect?: boolean
}

export const ChangePasswordComponent = ({redirect}: Props) => {
	const pwScore = useSelector(selPasswordScore);

	const [pwOld, setPwOld] = useState('');
	const [pw1, setPw1] = useState('');
	const [pw2, setPw2] = useState('');
	const [msg, setMsg] = useState<MsgProps | null>(null);
	const [pswVisibility, setPswVisibility] = useState<PwdStateType>({old: false, pw1: false, pw2: false});
	const [indicator, setIndicator] = useState<Indicator | null>();

	useEffect(() => {
		if (pw1 === "") return;
		setIndicator(zxcvbn(pw1));
	}, [pw1]);

	let loginScore = -1;
	if (pwScore != null) {
		loginScore = parseInt(pwScore);
	}
	const score = indicator ? indicator.score : -1;
	const feedback = indicator ? indicator.feedback : undefined;
	const isPswIndicatorVis = pw1 !== "" && loginScore >= 0;
	let disabled = !pw1.trim().length || !pw2.trim().length || pw1 !== pw2 || score < loginScore;
	const handlePswVisibility = (pw: string) => {
		let p = pswVisibility[pw];
		setPswVisibility(
			{
				...pswVisibility,
				[pw]: !p,
			},
		)
	};

	const clearMsg = useCallback(() => setMsg(null), []);

	const changePasswordHandler = useCallback(() => {
		const requestData = {
			current_password: pwOld,
			new_password: pw1,
			new_password_confirm: pw2,
		};
		axios.post("../json/evoAppsUserInfoDialogController/change-default-user-password", requestData)
			.then(() => {
				setPwOld('');
				setPw1('');
				setPw2('');
				setMsg({status: 'ok', text: "Passwort wurde geändert."});
				if (redirect) {
					window.location.replace(encodeURIComponent("home.jsp"));
				}
			}).catch(({response}) => {
			const text = response && response.data && response.data.message;
			setMsg({status: 'error', text: text || "Ein unerwarteter Fehler ist aufgetreten."});
		});
	}, [pw1, pw2, pwOld, redirect]);

	return (
		<form noValidate autoComplete="off" css={cssForm}>
			<TextField
				value={pwOld}
				onChange={useCallback<Required<TextFieldProps>['onChange']>((evt) => setPwOld(evt.target.value), [])}
				type={pswVisibility['old'] ? 'text' : 'password'}
				margin={'dense'}
				fullWidth
				label={'Aktuelles Passwort'}
				InputProps={{
					endAdornment: (
						<InputAdornment position="start" onClick={() => handlePswVisibility('old')}>
							{getVisibilityIcon(pswVisibility['old'])}
						</InputAdornment>
					),
				}}
			/>
			<TextField
				value={pw1}
				onChange={useCallback<Required<TextFieldProps>['onChange']>((evt) => setPw1(evt.target.value), [])}
				type={pswVisibility['pw1'] ? 'text' : 'password'}
				margin={'dense'}
				fullWidth
				label={'Neues Passwort'}
				InputProps={{
					endAdornment: (
						<InputAdornment position="start" onClick={() => handlePswVisibility('pw1')}>
							{getVisibilityIcon(pswVisibility['pw1'])}
						</InputAdornment>
					),
				}}
			/>

			{isPswIndicatorVis && <><IndicatorsComponent score={score}/> <PasswordScoreFeedbackComponent feedback={feedback}/></>}

			<TextField
				value={pw2}
				onChange={useCallback<Required<TextFieldProps>['onChange']>((evt) => setPw2(evt.target.value), [])}
				type={pswVisibility['pw2'] ? 'text' : 'password'}
				margin={'dense'}
				fullWidth
				label={'Neues Passwort bestätigen'}
				sx={{
					"& .MuiInput-root": {
						// Bottom border
						"&:before": {
							borderColor: getConfirmColor(pw1, pw2),
						},
						// Border on focus
						"&:after": {
							borderColor: getConfirmColor(pw1, pw2),
						},
						":hover:not(.Mui-focused)": {
							"&:before": {
								color: getConfirmColor(pw1, pw2),
							},
						},
					},
					// Label
					"& .MuiInputLabel-standard": {
						"&.Mui-focused": {
							color: getConfirmColor(pw1, pw2),
						},
					},
				}}
				InputProps={{
					endAdornment: (
						<InputAdornment position="start" onClick={() => handlePswVisibility('pw2')}>
							{getVisibilityIcon(pswVisibility['pw2'])}
						</InputAdornment>
					),
				}}
			/>
			{pw1 !== pw2 && <Container css={css`${cssContainer} ${cssErrorLabel}`}>
				Passwörter stimmen nicht übereich.
			</Container>}
			<Container css={cssContainer}>
				<Button onClick={changePasswordHandler} disabled={disabled}>Passwort ändern</Button>
				{redirect && <Button onClick={() => window.location.replace(encodeURIComponent("login.jsp"))}>Zurück zum Login</Button>}
			</Container>
			{msg &&
				<Container css={cssContainer}>
					<MessageChip variant={'warn'} label={msg.text} onClick={clearMsg}/>
				</Container>
			}
		</form>
	);
};